import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LinkButton from './LinkButton';
import WaveSurfer from 'wavesurfer.js';
import AudioUploader from './AudioUploader';
import AudioVersions from './AudioVersions';
import AudioEditorModal from './AudioEditorModal';
import StableLabel from './StableLabel';
import {
  fragmentFromSelection, fragmentTag, wordAtSelection, textWords, wordFace, bareOf, matchWords,
} from '../utils/wordMap';
import SelectionPopup from './SelectionPopup';
import StressReview from './StressReview';
import SpeechGeneration, { buildTtsRequest, loadLastTtsSettings } from './SpeechGeneration';
import { notifyTtsAttempt } from './TtsAttemptsChip';
import './AudioStudio.css';

// Full-screen voice-over studio: the text on the left, the track on the right.
//
// Everything it shows comes from one alignment record per audio version — a
// list of words, each with a character span in the spoken text and a start/end
// in seconds. The text is therefore readable prose that happens to know what
// time every word is at, which is what makes both directions of navigation
// possible: click a word to jump there, and let playback move the highlight.
//
// The text rendered here is the *_voiceover twin, not the source field: it is
// what was actually read out, stress marks included, and those marks are half
// the reason to come to this screen. Its "Speaker N:" labels and "[note]"
// directives are chrome for the TTS, never spoken, so they are drawn as a thin
// turn divider instead of as words.

const VOICE_FIELDS_KEYS = ['description', 'after_message'];
// Whether a point has anything to voice in a field (the twin or the source).
const fieldHasText = (point, f) => !!point
  && String(point[`${f}_voiceover`] || point[f] || '').trim().length > 0;

const FIELDS = [
  { key: 'description', label: 'Description' },
  { key: 'after_message', label: 'After message' },
];

// Sentence-ending marks always cut a phrase; the softer set is optional
// because how fine the highlight should be is a matter of taste.
const CUT_STRONG = '.!?…:;';
const CUT_SOFT = ',—–';
const MAX_PHRASE_WORDS = 12;

// "Speaker 1:" plus an optional "[firmly]" right after it.
const CHROME_RE = /^[ \t]*Speaker\s*(\d+)\s*:[ \t]*(\[[^\]\n]*\])?[ \t]*/gm;
const BRACKET_RE = /\[[^\]\n]*\]/g;

const fmt = (t) => {
  if (!Number.isFinite(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Always the time the take was made; a custom label is added next to it, not
// instead of it — two takes are told apart by when they were generated.
// The marks a word of the prose can carry, in the order the legend lists them.
// Shown only for marks present on screen.
const LEGEND = [
  { cls: 'as-w-repl', sample: 'word', label: 'rewritten on the way to TTS by a replacement rule (hover: what is sent)' },
  { cls: 'as-w-diff', sample: 'word', label: 'the text changed since this take (hover: how it was read)' },
  { cls: 'as-w-gone', sample: 'word', label: 'in the take, no longer in the text' },
  { cls: 'as-w-new', sample: 'word', label: 'in the text now, no audio in this take' },
  { cls: 'as-w-own', sample: 'word', label: 'stress marked on this one place, against the glossary' },
  { cls: 'as-w-stress', sample: 'word', label: 'stress check: heard differently than expected' },
  { cls: 'as-w-heard', sample: 'word', label: 'stress heard, nothing to compare it with' },
  { cls: 'as-w-dim', sample: 'word', label: 'outside the fragment this source voiced' },
];
const LEGEND_CLASSES = new Set(LEGEND.map((l) => l.cls));

const versionName = (v) => {
  const d = new Date(v.uploaded_at);
  const p = (n) => String(n).padStart(2, '0');
  const stamp = isNaN(d.getTime())
    ? (v.filename || `#${v.id}`)
    : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  const differ = v.words_differ > 0 ? ` · ${v.words_differ} word${v.words_differ === 1 ? '' : 's'} differ` : '';
  return `${v.label ? `${stamp} · ${v.label}` : stamp}${fragmentTag(v)}${differ}`;
};

// The generation request, plus the span for "voice selection only".
const withFragment = (req, frag) => (frag
  ? { ...req, fragment: { start: frag.start, end: frag.end, body: frag.body, prefix: frag.prefix } }
  : req);

// Ranges of the text that are chrome, so the reader never sees them as words.
function chromeRanges(text) {
  const out = [];
  for (const re of [CHROME_RE, BRACKET_RE]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      out.push({
        start: m.index, end: m.index + m[0].length,
        speaker: m[1] || null, note: m[2] || null,
      });
      if (m[0].length === 0) re.lastIndex += 1;
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

// Group words into phrases. A phrase ends when the text between this word and
// the next carries a cut mark, or when it has simply grown too long.
function buildPhrases(text, words, soft) {
  const cuts = CUT_STRONG + (soft ? CUT_SOFT : '');
  const phrases = [];
  let cur = [];
  words.forEach((w, i) => {
    cur.push(i);
    const next = words[i + 1];
    const between = text.slice(w.b, next ? next.a : text.length);
    if (!next || [...between].some((c) => cuts.includes(c)) || cur.length >= MAX_PHRASE_WORDS) {
      phrases.push(cur);
      cur = [];
    }
  });
  if (cur.length) phrases.push(cur);
  // A one- or two-word leftover reads as part of its neighbour, not as a unit.
  const merged = [];
  phrases.forEach((p) => {
    if (merged.length && p.length < 3) merged[merged.length - 1].push(...p);
    else merged.push(p);
  });
  return merged;
}

// A run's progress: a bar plus words — never the bar alone. The server sends
// the stage, the seconds so far and its estimate of the rest at each stage
// change; between those the numbers run on the local clock.
function AlignProgress({ entry, what, flat }) {
  if (!entry || (entry.status !== 'running' && entry.status !== 'queued')) return null;
  const p = entry.progress;
  const since = (Date.now() - (entry.at || Date.now())) / 1000;
  let text;
  let frac = 0;
  if (entry.status === 'queued' || !p) {
    text = 'in the queue, waiting for a slot';
  } else {
    const elapsed = Math.round(p.elapsed + since);
    const eta = Math.max(0, Math.round(p.eta - since));
    frac = eta > 0 ? Math.min(0.98, elapsed / (elapsed + eta)) : 0.98;
    text = `${p.label} · ${elapsed}s${eta > 0 ? ` · ~${eta}s left` : ' · any moment now'}`;
  }
  return (
    <div className={`as-progress${flat ? ' as-progress-flat' : ''}`} role="status">
      <span className="as-progress-text">{what ? `${what} · ` : ''}measuring timings · {text}</span>
      <span className="as-progress-bar"><span className="as-progress-fill" style={{ width: `${Math.round(frac * 100)}%` }} /></span>
    </div>
  );
}

// The word with a stress mark on its vowel number `vi` (0-based), marks it had dropped.
const STRESS_VOWELS = 'аеёиоуыэюяАЕЁИОУЫЭЮЯ';
function withStress(word, vi) {
  const plain = String(word || '').replace(/\u0301/g, '');
  if (vi == null) return plain;
  let n = -1;
  let out = '';
  for (const ch of plain) {
    out += ch;
    if (STRESS_VOWELS.includes(ch)) { n += 1; if (n === vi) out += '\u0301'; }
  }
  return out;
}

export default function AudioStudio({
  questId, questName, locations, initialLocationUuid, onBack, backHref, onNavigateToLocation,
  onSaveVoiceoverText, initialField, initialTab, initialVersionId = null, onStateChange, taskCountsByLocation = null, tasksHref = null,
  // {id, back}: the editor saves into this Draft of a turn (Tasks screen) instead of as a take
  initialDraft = null,
}) {
  const points = useMemo(
    () => (locations || []).filter((l) => l && l.uuid), [locations]
  );

  const [locUuid, setLocUuid] = useState(
    initialLocationUuid || (points[0] && points[0].uuid) || null
  );
  const [field, setField] = useState(initialField || 'description');
  // The field picked in the header outlives the point. Landing on a point that
  // has no such field — many have no after message — showed an empty screen
  // that looked like a failed load; now the field with text is picked instead.
  // Only on a point change: a field chosen by hand on this point is respected.
  const lastLocForField = useRef(null);
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(null);
  const [data, setData] = useState(null);          // server payload for the version
  const [coverage, setCoverage] = useState([]);    // per-version rail badges
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [follow, setFollow] = useState(true);      // auto-scroll with playback
  const [loop, setLoop] = useState(null);          // {wi0, wi1, start, end} from a selection
  // Two panes only: the editor and the generator. What used to be read-only
  // lists (Phrases, Check first) now happens where the reading happens — in
  // the popup a selection opens over the text.
  const [tab, setTab] = useState(initialTab === 'tts' ? 'tts' : initialTab === 'review' ? 'review' : 'splice');
  // A review row asked to be opened: {versionId, locationUuid, fieldType, wordIndex, start, end}.
  const pendingOpenRef = useRef(null);
  // the take the address names (?version=): selected on the first load of the
  // field's takes only, a later field or point switch picks the active one
  const initialVersionRef = useRef(initialVersionId || null);
  const [playingKey, setPlayingKey] = useState(null);
  // Where that popup sits, or null while nothing is selected.
  const [popupAt, setPopupAt] = useState(null);
  // Cancel in the editor reloads the track instead of leaving the pane.
  const [editorNonce, setEditorNonce] = useState(0);
  const [align_, setAlignRun] = useState(null);  // {status, elapsed, error, output}
  const [take, setTake] = useState(null);         // one-click donor take: {status, elapsed, versionId, error, details}
  // versionId -> {status, progress, at}: queued | running | done | failed,
  // live from the server, with the run's stage and estimate while it runs.
  const [alignStatus, setAlignStatus] = useState({});
  const alignStatusRef = useRef({});
  const [tick, setTick] = useState(0);   // once a second while a run is on
  // A donor that voices only part of the text, as the editor lines it up:
  // {versionId, toSource, lo, hi, changed}. The prose dims what it lacks.
  const [donorMap, setDonorMap] = useState(null);
  // A fragment take just made, handed to the editor to open as the donor.
  const [donorRequest, setDonorRequest] = useState(null);
  const pendingDonorRef = useRef(null);   // stored, waiting for its timings
  const autoSelectRef = useRef(null);     // opened, waiting to select its words
  const placeRef = useRef('');            // "uuid/field" on screen now
  const versionIdRef = useRef(null);

  const audioRef = useRef(null);
  const waveBox = useRef(null);
  const wave = useRef(null);
  const textBox = useRef(null);
  // Set only while a selection is being played on purpose: {start, end, loop}.
  // Ordinary playback must NOT be pulled back by whatever happens to be
  // selected — that made a plain pause look like the cursor jumping away.
  const selPlayRef = useRef(null);
  const followRef = useRef(true);
  useEffect(() => { selPlayRef.current = null; }, [loop]);
  useEffect(() => { followRef.current = follow; }, [follow]);

  const pointForField = useMemo(
    () => (locations || []).find((l) => l && l.uuid === locUuid) || null, [locations, locUuid]
  );
  useEffect(() => {
    if (!pointForField || lastLocForField.current === locUuid) return;
    lastLocForField.current = locUuid;
    if (fieldHasText(pointForField, field)) return;
    const other = VOICE_FIELDS_KEYS.find((f) => f !== field && fieldHasText(pointForField, f));
    if (other) setField(other);
  }, [locUuid, pointForField, field]);

  const point = useMemo(
    () => points.find((p) => p.uuid === locUuid) || null, [points, locUuid]
  );
  const pointIndex = useMemo(
    () => points.findIndex((p) => p.uuid === locUuid), [points, locUuid]
  );

  // ---- data ---------------------------------------------------------------
  const loadCoverage = useCallback(() => {
    if (!questId) return;
    fetch(`/api/quest-alignments/${encodeURIComponent(questId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then(setCoverage)
      .catch(() => setCoverage([]));
  }, [questId]);
  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  // A glossary save anywhere changes how every text reads (the glossary is a
  // rule applied on the server, never written into the texts): re-read the
  // field on screen and the per-point counters.
  const [readingTick, setReadingTick] = useState(0);
  useEffect(() => {
    const onGlossary = () => { setReadingTick((n) => n + 1); loadCoverage(); };
    window.addEventListener('qv:glossary', onGlossary);
    return () => window.removeEventListener('qv:glossary', onGlossary);
  }, [loadCoverage]);

  const loadVersions = useCallback((keepId) => {
    if (!locUuid) return Promise.resolve();
    return fetch(`/api/audio-versions/${locUuid}/${field}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        setVersions(list);
        const keep = keepId && list.some((v) => v.id === keepId) ? keepId : null;
        const active = list.find((v) => v.is_active) || list[list.length - 1];
        setVersionId(keep || (active ? active.id : null));
        return list;
      })
      .catch(() => { setVersions([]); return []; });
  }, [locUuid, field]);

  // Passing notes, bottom right: «timings are in», «the take is ready». They
  // say it and go; nothing on screen moves because of them — the editor used
  // to reset the pick when timings landed, and that is what this replaces.
  const [notices, setNotices] = useState([]);
  const noticeSeq = useRef(0);
  const dismissNotice = useCallback((id) => setNotices((l) => l.filter((n) => n.id !== id)), []);
  const notify = useCallback((text, kind = 'ok') => {
    const id = ++noticeSeq.current;
    setNotices((l) => [...l.slice(-4), { id, text, kind }]);
    setTimeout(() => dismissNotice(id), kind === 'warn' ? 15000 : 8000);
  }, [dismissNotice]);

  useEffect(() => {
    setVersions([]);
    setVersionId(null);
    const wanted = initialVersionRef.current;
    initialVersionRef.current = null;
    loadVersions(pendingOpenRef.current ? pendingOpenRef.current.versionId : (wanted || undefined));
  }, [loadVersions]);

  useEffect(() => {
    if (!versionId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/audio-alignment/${versionId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load alignment'))))
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setData(null); setError(e.message); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [versionId]);

  const alignment = data && data.alignment;
  const words = useMemo(() => (alignment && alignment.words) || [], [alignment]);
  const text = (alignment && alignment.text) || (data && data.sourceText) || '';
  // Machine stress check of this take (scripts/align/stress_check.py), by word ordinal.
  const stressByWord = useMemo(() => {
    const m = new Map();
    ((data && data.stress && data.stress.words) || []).forEach((r) => m.set(r.w, r));
    return m;
  }, [data]);
  const [stressOnly, setStressOnly] = useState(false);
  const stressMismatches = useMemo(() => [...stressByWord.entries()]
    .filter(([wi, r]) => r.verdict === 'mismatch' && words[wi])
    .map(([wi, r]) => ({ wi, r }))
    .sort((x, y) => (y.r.conf ?? 0) - (x.r.conf ?? 0)), [stressByWord, words]);

  // Measuring timings takes minutes, so the request only starts the run and
  // hands back a ticket; this polls it and reloads the alignment when it lands.
  const runAlign = useCallback(async () => {
    if (!versionId) return;
    setAlignRun({ status: 'running', elapsed: 0 });
    try {
      const r = await fetch(`/api/audio-alignment/${versionId}/run`, {
        method: 'POST', credentials: 'include',
      });
      const started = await r.json();
      if (!r.ok) { setAlignRun({ status: 'failed', error: started.error }); return; }
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/audio-alignment/run/${started.taskId}`, { credentials: 'include' });
          const t = await pr.json();
          if (t.status === 'running' || t.status === 'queued') { setAlignRun(t); return; }
          clearInterval(poll);

          // A run record lives in the server's memory, so a restart mid-run
          // takes it with it and this answer carries no status at all. That is
          // not proof the work failed — check whether the timings actually
          // landed before saying anything.
          const a = await fetch(`/api/audio-alignment/${versionId}`, { credentials: 'include' });
          const fresh = a.ok ? await a.json() : null;
          const landed = !!(fresh && fresh.alignment);

          if (t.status === 'done' || (!t.status && landed)) {
            if (fresh) setData(fresh);
            loadCoverage();
            loadVersions(versionId);
            setAlignRun({ status: 'done' });
            return;
          }
          setAlignRun({
            status: 'failed',
            error: t.error || 'The run stopped without finishing — the server may have restarted.',
            output: t.output,
          });
          setAlignStatus((m) => ({ ...m, [versionId]: { status: 'failed', error: t.error || null, at: Date.now() } }));
        } catch (e) { /* one missed poll is not a failure */ }
      }, 3000);
    } catch (e) {
      setAlignRun({ status: 'failed', error: e.message });
    }
  }, [versionId, loadCoverage, loadVersions]);

  // Hand the position up so it can live in the address bar: a refresh, a
  // bookmark or a link then lands on the same point, field and tab.
  useEffect(() => {
    if (onStateChange) onStateChange({ locUuid, field, tab });
  }, [locUuid, field, tab, onStateChange]);

  // Memoised for the same reason as `align` below: the generation panel
  // re-seeds its whole state from this prop, so a fresh object each render
  // erased what the user was doing, including the "Generating…" state.
  const ttsData = useMemo(() => (point ? {
    text: point[`${field}_voiceover`] || point[field] || '',
    locationId: point.id,
    locationUuid: point.uuid,
    field,
    locationName: point.name,
    locationOrder: pointIndex >= 0 ? pointIndex + 1 : null,
    questId,
  } : null), [point, field, pointIndex, questId]);

  useEffect(() => { versionIdRef.current = versionId; }, [versionId]);

  // A fragment take never waits in the version list for someone to find it:
  // once it has timings it opens as the donor, its words already selected.
  const openAsDonor = useCallback((id) => {
    pendingDonorRef.current = null;
    autoSelectRef.current = id;
    setDonorRequest({ versionId: id, n: Date.now() });
  }, []);

  useEffect(() => {
    placeRef.current = `${locUuid}/${field}`;
    setDonorRequest(null);
    pendingDonorRef.current = null;
    autoSelectRef.current = null;
  }, [locUuid, field]);

  // The server announces every change in a version's timings. Refreshing on
  // those is what lets a freshly generated take become selectable as a donor
  // without reloading the page.
  useEffect(() => {
    const onAlign = (e) => {
      const d = (e && e.detail) || {};
      if (!d.versionId) return;
      const entry = {
        status: d.status, progress: d.progress || null,
        error: d.error || null, output: d.output || null, at: Date.now(),
      };
      setAlignStatus((m) => ({ ...m, [d.versionId]: entry }));
      alignStatusRef.current = { ...alignStatusRef.current, [d.versionId]: entry };
      if (d.status !== 'done' && d.status !== 'failed') return;
      if (d.versionId === pendingDonorRef.current) {
        if (d.status === 'done') openAsDonor(d.versionId);
        else pendingDonorRef.current = null;
      }
      loadVersions(versionIdRef.current).then((list) => {
        // Only this point's takes: the event goes to the whole quest, and a
        // batch run elsewhere in it is not this screen's news.
        const v = (list || []).find((x) => x.id === d.versionId);
        if (!v) return;
        if (d.status === 'done') notify(`Timings are in · ${versionName(v)}`);
        else notify(`Timings failed · ${versionName(v)}${d.error ? ` — ${d.error}` : ''}`, 'warn');
      });
      loadCoverage();
      if (d.status === 'done' && d.versionId === versionIdRef.current) {
        fetch(`/api/audio-alignment/${d.versionId}`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((x) => { if (x) setData(x); })
          .catch(() => { /* the next event or a reload will catch up */ });
      }
    };
    window.addEventListener('qv:alignment', onAlign);
    return () => window.removeEventListener('qv:alignment', onAlign);
  }, [loadVersions, loadCoverage, openAsDonor, notify]);

  // Another take of the same text with the settings last used in the
  // generation panel, stored WITHOUT replacing the active one — it exists to be
  // a donor. It still queues itself for timings on the server.
  // With `frag` (from fragmentFromSelection) only those words are voiced.
  const newTake = useCallback(async (frag = null) => {
    if (!point) return;
    const text = frag ? frag.text : (point[`${field}_voiceover`] || point[field] || '');
    if (!text.trim()) { setTake({ status: 'failed', error: 'This field has no text to voice.' }); return; }
    const place = `${point.uuid}/${field}`;
    const isFrag = !!frag;
    const started = Date.now();
    setTake({ status: 'running', elapsed: 0, fragment: isFrag });
    try {
      const r = await fetch('/api/generate-speech-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(withFragment(buildTtsRequest({
          text,
          settings: loadLastTtsSettings(),
          locationUuid: point.uuid,
          fieldType: field,
          questId,
          activate: false,
        }), frag)),
      });
      const start = await r.json().catch(() => ({}));
      if (!r.ok) {
        setTake({ status: 'failed', error: start.error || `HTTP ${r.status}`, details: start.details || null });
        return;
      }
      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const pr = await fetch(`/api/generate-speech-task/${start.taskId}`, { credentials: 'include' });
        if (pr.status === 404) {
          // The task lived in the server's memory; a restart took it. The take
          // may still have landed, so say where to look instead of spinning.
          setTake({ status: 'failed', error: 'The generation task vanished (server restarted?). Check the version list — the take may still be there.' });
          loadVersions(versionId);
          return;
        }
        const t = await pr.json().catch(() => ({}));
        if (t.status === 'running' || !t.status) {
          setTake({ status: 'running', elapsed: Math.round((Date.now() - started) / 1000), fragment: isFrag });
          continue;
        }
        if (t.status === 'done') {
          setTake({ status: 'done', versionId: t.versionId, fragment: isFrag });
          notify(`${isFrag ? 'Fragment' : 'Voice-over'} ready · version ${t.versionId} · timings on their way`);
          if (isFrag && placeRef.current === place) {
            if ((alignStatusRef.current[t.versionId] || {}).status === 'done') openAsDonor(t.versionId);
            else pendingDonorRef.current = t.versionId;
          }
          loadVersions(versionId);          // keep the take being edited selected
          loadCoverage();
          return;
        }
        setTake({ status: 'failed', error: t.error || 'Generation failed', details: t.details || null });
        return;
      }
      setTake({ status: 'failed', error: 'Timed out waiting for the generation (20 min).' });
    } catch (e) {
      setTake({ status: 'failed', error: e.message || String(e) });
    } finally {
      notifyTtsAttempt();
    }
  }, [point, field, questId, versionId, loadVersions, loadCoverage, openAsDonor, notify]);

  // Must be memoised: the editor watches this object, and a new one per render
  // would restart its effects forever.
  const align = useMemo(
    () => (alignment ? { words, silence: (alignment.stats || {}).silence, text } : null),
    [alignment, words, text]
  );

  // The point's text as stored: only the marks put on single places.
  const ownText = point
    ? (String(point[`${field}_voiceover`] || '').trim() || String(point[field] || '').trim())
    : '';
  // How it reads — the glossary applied to every word it leaves unmarked —
  // and where those one-place marks sit, from the server, where the glossary
  // rule lives. Re-read whenever the stored text or the glossary changes; the
  // last reading stays on screen meanwhile.
  const [reading, setReading] = useState(null);     // {uuid, field, text, ownMarks}
  const [readingError, setReadingError] = useState(null);
  const pointUuid = point ? point.uuid : null;
  useEffect(() => {
    if (!questId || !pointUuid) { setReading(null); return undefined; }
    let dead = false;
    fetch(`/api/pipeline/quest/${encodeURIComponent(questId)}/voiceover-reading?uuid=${encodeURIComponent(pointUuid)}&field=${field}`,
      { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        return d;
      })
      .then((d) => { if (!dead) { setReading({ uuid: pointUuid, field, ...d }); setReadingError(null); } })
      .catch((e) => { if (!dead) setReadingError(`Could not read how this text is voiced: ${e.message}`); });
    return () => { dead = true; };
  }, [questId, pointUuid, field, ownText, readingTick]);
  // The words picked in the prose, as "voice selection only" would send them.
  const currentText = reading && reading.uuid === pointUuid && reading.field === field ? reading.text : '';
  // Per word of the current text: the mark stored on that one place, if any.
  // `against` (non-null) is what the glossary says for the form when it says
  // something else — those places are drawn apart in the prose.
  const ownMarks = useMemo(() => {
    const marks = (reading && reading.ownMarks) || [];
    if (!currentText || !marks.length) return [];
    return textWords(currentText).map((w) => marks.find((m) => m.a >= w.a && m.b <= w.b) || null);
  }, [currentText, reading]);
  // Per word of the current text: the replacement rule that rewrites it on the
  // way to TTS ({a, b, to}), if any. The prose keeps the text itself; the hint
  // says what is sent instead.
  const replacedWords = useMemo(() => {
    const spans = (reading && reading.replaced) || [];
    if (!currentText || !spans.length) return [];
    return textWords(currentText).map((w) => spans.find((r) => r.a < w.b && r.b > w.a) || null);
  }, [currentText, reading]);
  // Two texts live here: the one the take was voiced from (timings, clicks,
  // verdicts all sit on it) and the point's text TODAY (what a re-voice, a
  // stress fix or the glossary would use). The prose shows the take's words
  // as today's text spells them, and marks every place the two disagree:
  // a word that changed, a word the take still says but the text dropped,
  // a word the text gained that no audio has. «As voiced» shows the take's
  // own text verbatim instead.
  const [asVoiced, setAsVoiced] = useState(false);
  const textDiff = useMemo(() => {
    if (!currentText || !words.length) return null;
    const cur = textWords(currentText).map((t) => wordFace(currentText.slice(t.a, t.b)));
    const voiced = words.map((w) => text.slice(w.a, w.b));
    // Matched on the bare form that keeps «ё» apart from «е»: «нем» → «нём»
    // is exactly the kind of edit this has to show, and the server counts it
    // the same way. Stress marks are dropped for the match — a moved mark is
    // still the same word, shown with its new mark and a hint.
    const map = matchWords(voiced.map(bareOf), cur.map(bareOf));
    const perWord = new Array(words.length);
    const insertedAfter = new Map();   // take word index (-1 = before the first) -> [spellings]
    const anchors = [];
    map.forEach((j, i) => { if (j >= 0) anchors.push([i, j]); });
    anchors.push([words.length, cur.length]);
    let prevT = -1;
    let prevC = -1;
    let differ = 0;
    for (const [ti, cj] of anchors) {
      const tRun = [];
      for (let i = prevT + 1; i < ti; i += 1) tRun.push(i);
      const cRun = [];
      for (let j = prevC + 1; j < cj; j += 1) cRun.push(j);
      const n = Math.min(tRun.length, cRun.length);
      for (let k = 0; k < n; k += 1) { perWord[tRun[k]] = { shown: cur[cRun[k]], cj: cRun[k], changed: true }; differ += 1; }
      for (let k = n; k < tRun.length; k += 1) { perWord[tRun[k]] = { shown: voiced[tRun[k]], gone: true }; differ += 1; }
      if (cRun.length > n) {
        insertedAfter.set(tRun.length ? tRun[tRun.length - 1] : prevT, cRun.slice(n).map((j) => cur[j]));
        differ += cRun.length - n;
      }
      if (ti < words.length) perWord[ti] = { shown: cur[cj], cj, changed: false };
      prevT = ti;
      prevC = cj;
    }
    return { perWord, insertedAfter, differ };
  }, [currentText, text, words]);

  const fragPlan = useMemo(() => (loop && words.length && currentText
    ? fragmentFromSelection({ shownText: text, shownWords: words, wi0: loop.wi0, wi1: loop.wi1, currentText })
    : null), [loop, words, text, currentText]);

  // A one-word selection is also a word: its stress, its glossary entry and
  // every other place it is spoken in the quest. Taken from the point's
  // CURRENT text — the take on screen may be older than the prose.
  // `place` names it for a one-place stress mark (the same span a fragment
  // take of it would send); `ownMark` is the mark stored on it, if any.
  const selectedWord = useMemo(() => {
    if (!loop || loop.wi0 !== loop.wi1 || !words.length || !currentText || !fragPlan) return null;
    const w = wordAtSelection({ shownText: text, shownWords: words, wi: loop.wi0, currentText });
    if (!w) return null;
    const cj = textWords(currentText).findIndex((t) => t.a === fragPlan.start);
    const own = cj >= 0 ? ownMarks[cj] : null;
    return {
      ...w,
      place: { locationUuid: point.uuid, field, start: fragPlan.start, end: fragPlan.end, body: fragPlan.body },
      ownMark: own ? own.spelling : null,
    };
  }, [loop, words, text, currentText, fragPlan, ownMarks, point, field]);

  // A fragment just opened as the donor: select its words, which puts a
  // region on both tracks — what is left is to listen and replace.
  useEffect(() => {
    if (!donorMap || donorMap.versionId !== autoSelectRef.current) return;
    autoSelectRef.current = null;
    setDonorRequest(null);
    const a = words[donorMap.lo];
    const b = words[donorMap.hi];
    if (donorMap.lo >= 0 && a && b) setLoop({ wi0: donorMap.lo, wi1: donorMap.hi, start: a.s, end: b.e });
  }, [donorMap, words]);

  const phrases = useMemo(
    () => (words.length ? buildPhrases(text, words, true) : []), [text, words]
  );
  const phraseOfWord = useMemo(() => {
    const m = new Int32Array(words.length).fill(-1);
    phrases.forEach((p, pi) => p.forEach((wi) => { m[wi] = pi; }));
    return m;
  }, [phrases, words.length]);

  // ---- audio --------------------------------------------------------------
  const src = versionId ? `/api/audio-alignment/${versionId}/audio` : null;

  // The waveform drives, and is driven by, the same <audio> element the
  // transport uses, so the two can never disagree about the position. It is
  // created once and then re-pointed: destroying it on every version change
  // pulled the media element out from under itself and left a blank canvas.
  useEffect(() => {
    if (wave.current || !waveBox.current || !audioRef.current) return undefined;
    const ws = WaveSurfer.create({
      container: waveBox.current,
      media: audioRef.current,
      height: 84,
      waveColor: '#8fa3bf',
      progressColor: '#3d5a80',
      cursorColor: '#c1121f',
      cursorWidth: 2,
      normalize: true,
    });
    wave.current = ws;
    return () => { try { ws.destroy(); } catch (e) { /* already gone */ } wave.current = null; };
  }, []);

  // Loading through wavesurfer (rather than an src on the <audio> tag) is what
  // gives it bytes to decode the peaks from; it sets the element's source
  // itself, so there is still exactly one player.
  useEffect(() => {
    const ws = wave.current;
    if (!ws) return;
    if (!src) { try { ws.empty(); } catch (e) { /* nothing loaded yet */ } return; }
    const p = ws.load(src);
    if (p && typeof p.catch === 'function') p.catch(() => { /* switched away mid-load */ });
  }, [src]);

  // The review tab hands a row here: switch the studio to that take and, once
  // it is loaded, play the word with a little context, highlighted as a selection.
  const openReviewRow = useCallback((r) => {
    pendingOpenRef.current = r;
    setPlayingKey(`${r.versionId}/${r.wordIndex}`);
    if (r.locationUuid !== locUuid) setLocUuid(r.locationUuid);
    if (r.fieldType !== field) setField(r.fieldType);
    if (r.versionId !== versionId) setVersionId(r.versionId);
    else setTick((n) => n + 1);   // same take: the effect below runs again
  }, [locUuid, field, versionId]);
  // Two steps on purpose: the selection is set first, and the play starts from
  // an effect on `loop` that runs AFTER the one above which clears the stop
  // marker whenever the selection changes — otherwise the marker was wiped the
  // moment it was set and the take played on past the word.
  const reviewPlayRef = useRef(null);
  useEffect(() => {
    const r = pendingOpenRef.current;
    if (!r || !data || data.versionId !== r.versionId || !duration) return;
    const start = Math.max(0, (r.start ?? 0) - 0.6);
    const end = Math.min(duration, (r.end ?? start) + 0.6);
    pendingOpenRef.current = null;
    reviewPlayRef.current = { start, end };
    setLoop((cur) => (cur && cur.wi0 === r.wordIndex && cur.wi1 === r.wordIndex && cur.start === start && cur.end === end
      ? { ...cur } : { wi0: r.wordIndex, wi1: r.wordIndex, start, end }));
  }, [data, duration, tick]);
  useEffect(() => {
    const p = reviewPlayRef.current;
    const a = audioRef.current;
    if (!p || !a) return;
    reviewPlayRef.current = null;
    a.currentTime = p.start;
    setTime(p.start);
    selPlayRef.current = { start: p.start, end: p.end, loop: false };
    a.play().catch(() => {});
  }, [loop]);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(t)) return;
    selPlayRef.current = null;      // going somewhere by hand ends a selection play
    a.currentTime = Math.max(0, t);
    setTime(a.currentTime);
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { selPlayRef.current = null; a.play().catch(() => {}); } else a.pause();
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.playbackRate = rate;
  }, [rate, src]);

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a) return;
    const sp = selPlayRef.current;
    if (sp && a.currentTime >= sp.end) {
      if (sp.loop) a.currentTime = sp.start;
      else { a.pause(); selPlayRef.current = null; }
    }
    setTime(a.currentTime);
  };

  // ---- current phrase, and keeping it in view ------------------------------
  const activeWord = useMemo(() => {
    if (!words.length) return -1;
    let lo = 0, hi = words.length - 1, best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (words[mid].s <= time) { best = mid; lo = mid + 1; } else { hi = mid - 1; }
    }
    return best;
  }, [words, time]);
  const activePhrase = activeWord >= 0 ? phraseOfWord[activeWord] : -1;

  useEffect(() => {
    if (!followRef.current || activePhrase < 0 || !textBox.current) return;
    const el = textBox.current.querySelector(`[data-phrase="${activePhrase}"]`);
    if (!el) return;
    const box = textBox.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const want = box.top + box.height * 0.4;
    if (Math.abs(r.top - want) > box.height * 0.25) {
      textBox.current.scrollTop += r.top - want;
    }
  }, [activePhrase]);

  // ---- selection -> play just that ----------------------------------------
  const onTextMouseUp = () => {
    const sel = window.getSelection();
    const drop = () => { setLoop(null); setPopupAt(null); };
    if (!sel || sel.isCollapsed || !textBox.current) { drop(); return; }
    const spans = [...textBox.current.querySelectorAll('[data-w]')].filter(
      (el) => sel.containsNode(el, true)
    );
    if (spans.length < 1) { drop(); return; }
    const idx = spans.map((el) => Number(el.dataset.w));
    // The pick is exactly what was highlighted, even with a fragment donor
    // open: a re-voice takes all of it, and the editor puts the donor's
    // region on whatever part of it the donor has.
    const wi0 = Math.min(...idx);
    const wi1 = Math.max(...idx);
    const a = words[wi0];
    const b = words[wi1];
    if (!a || !b) { drop(); return; }
    setLoop({ wi0, wi1, start: a.s, end: b.e });
    // The panel opens where the reading stopped, under the selection itself.
    try {
      const r = sel.getRangeAt(0).getBoundingClientRect();
      setPopupAt({ x: r.left, top: r.top, bottom: r.bottom });
    } catch (e) { setPopupAt(null); }
  };

  // The selection, once through (the panel's ▶) or round and round (the ↻
  // button next to the transport).
  const playSelection = useCallback((again) => {
    if (!loop) return;
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, loop.start);
    setTime(a.currentTime);
    selPlayRef.current = { start: loop.start, end: loop.end, loop: !!again };
    a.play().catch(() => {});
  }, [loop]);
  const playLoop = () => playSelection(true);

  // ---- rendering the text -------------------------------------------------
  // -> {nodes, used}: the prose, and which word marks it carries (the legend
  // under the text lists exactly those).
  const rendered = useMemo(() => {
    if (!text) return null;
    if (!words.length) return { nodes: <p className="as-plain">{text}</p>, used: new Set() };
    const chrome = chromeRanges(text);
    const nodes = [];
    const used = new Set();
    let cursor = 0;
    // A fragment donor read by the other speaker: every turn it reaches into
    // is labelled with both — the take's speaker, then the donor's.
    const donorSpan = donorMap && donorMap.speaker && donorMap.lo >= 0 && words[donorMap.lo] && words[donorMap.hi]
      ? { a: words[donorMap.lo].a, b: words[donorMap.hi].b, speaker: donorMap.speaker }
      : null;
    const turns = chrome.filter((c) => c.speaker);
    const whoReads = (c) => {
      const own = Number(c.speaker);
      if (!donorSpan || donorSpan.speaker === own) return { label: `Speaker ${c.speaker}`, title: null };
      const k = turns.indexOf(c);
      const turnEnd = k >= 0 && turns[k + 1] ? turns[k + 1].start : text.length;
      if (donorSpan.b <= c.end || donorSpan.a >= turnEnd) return { label: `Speaker ${c.speaker}`, title: null };
      return {
        label: `Speaker ${own} / ${donorSpan.speaker}`,
        title: `This take: Speaker ${own} · the fragment on the lower track: Speaker ${donorSpan.speaker}`,
      };
    };
    const pushGap = (from, to) => {
      let i = from;
      for (let k = 0; k < chrome.length; k += 1) {
        const c = chrome[k];
        if (c.start < i || c.start >= to) continue;
        if (c.start > i) nodes.push(text.slice(i, c.start));
        // A turn break says WHO reads what follows, and in what manner if the
        // text asked for one; a bare note in mid-speech stays inline.
        nodes.push(c.speaker ? (
          <span className="as-turn" key={`t${c.start}`}>
            <span className="as-turn-line" />
            <span className="as-turn-who" title={whoReads(c).title || undefined}>
              {whoReads(c).label}{c.note ? ` · ${c.note}` : ''}
            </span>
          </span>
        ) : (
          <span className="as-note-inline" key={`t${c.start}`}>{text.slice(c.start, c.end).trim()}</span>
        ));
        i = Math.max(c.end, c.start + 1);
      }
      if (i < to) nodes.push(text.slice(i, to));
    };
    words.forEach((w, wi) => {
      if (w.a > cursor) pushGap(cursor, w.a);
      const pi = phraseOfWord[wi];
      const cls = [
        'as-w',
        pi === activePhrase ? 'as-w-live' : '',
        loop && wi >= loop.wi0 && wi <= loop.wi1 ? 'as-w-sel' : '',
        donorMap && donorMap.toSource[wi] < 0 ? 'as-w-dim' : '',
        stressByWord.get(wi)?.verdict === 'mismatch' ? 'as-w-stress' : '',
        stressByWord.get(wi)?.verdict === 'no_expect' && stressByWord.get(wi)?.heard != null ? 'as-w-heard' : '',
        stressOnly && stressByWord.get(wi)?.verdict !== 'mismatch' ? 'as-w-quiet' : '',
      ].filter(Boolean).join(' ');
      const voiced = text.slice(w.a, w.b);
      const sc = stressByWord.get(wi);
      const d = !asVoiced && textDiff ? textDiff.perWord[wi] : null;
      const shown = d ? d.shown : voiced;
      const differs = shown.toLowerCase() !== voiced.toLowerCase();   // case alone is no change
      // A mark put on this one place that the glossary rules otherwise.
      const own = d && !d.gone && d.cj != null ? ownMarks[d.cj] : null;
      const against = own && own.against != null;
      const repl = d && !d.gone && d.cj != null ? replacedWords[d.cj] : null;
      const marks = [
        d && d.gone ? 'as-w-gone' : differs ? 'as-w-diff' : '',
        against ? 'as-w-own' : '',
        repl ? 'as-w-repl' : '',
      ].filter(Boolean);
      for (const c of [...cls.split(' '), ...marks]) if (LEGEND_CLASSES.has(c)) used.add(c);
      const tip = `${fmt(w.s)} · ${w.c.toFixed(2)}`
        + (repl ? ` · sent to TTS as «${repl.to}» (replacement rule)` : '')
        + (d && d.gone ? ' · the take says this word, the text no longer has it'
          : differs ? ` · voiced as «${voiced}»` : '')
        + (against ? ` · stressed here only — the glossary says «${own.against || '∅ no mark'}»` : '')
        + (sc && sc.verdict === 'mismatch'
          ? ` · stress: the text says «${withStress(voiced, sc.exp)}»${sc.src === 'acute' ? ' (glossary/mark)' : ''}, heard «${withStress(voiced, sc.heard)}» (${sc.conf != null ? sc.conf.toFixed(2) : '?'})`
          : sc && sc.verdict === 'no_expect' && sc.heard != null ? ` · stress heard: «${withStress(voiced, sc.heard)}» (no expectation to compare)` : '');
      if (wi === 0 && d && textDiff.insertedAfter.has(-1)) {
        used.add('as-w-new');
        nodes.push(<span key="ins-1" className="as-w-new" title="in the text now, not in this take">{textDiff.insertedAfter.get(-1).join(' ')} </span>);
      }
      nodes.push(
        <span
          key={`w${wi}`}
          className={[cls, ...marks].join(' ')}
          data-w={wi}
          data-phrase={pi}
          title={tip}
          onClick={() => seek(w.s)}
        >
          {shown}
        </span>
      );
      if (d && textDiff.insertedAfter.has(wi)) {
        used.add('as-w-new');
        nodes.push(<span key={`ins${wi}`} className="as-w-new" title="in the text now, not in this take"> {textDiff.insertedAfter.get(wi).join(' ')}</span>);
      }
      // The aligner's span stops before a trailing stress mark, so the take's
      // own mark sits in the gap after the word. When the word above is drawn
      // in today's spelling, that mark belongs to the take, not to what is
      // shown — skip it, or a word stressed on its last letter gets it twice
      // («Корню́́»), and a mark moved elsewhere stays behind («ко́рню́»).
      let next = w.b;
      if (d && !d.gone) while (text[next] === '\u0301') next += 1;
      cursor = next;
    });
    if (cursor < text.length) pushGap(cursor, text.length);
    return { nodes, used };
  }, [text, words, phraseOfWord, activePhrase, loop, seek, donorMap, textDiff, asVoiced, ownMarks, replacedWords, stressByWord, stressOnly]);

  // ---- rail badges --------------------------------------------------------
  const covByLoc = useMemo(() => {
    const m = {};
    coverage.forEach((c) => {
      m[c.locationUuid] = m[c.locationUuid] || {};
      m[c.locationUuid][c.fieldType] = c;
    });
    return m;
  }, [coverage]);

  // Words a human marked as NOT said the way the text spells them: what still
  // wants re-voicing on this point.
  const wrongOf = (uuid) => VOICE_FIELDS_KEYS
    .reduce((n, f) => n + (((covByLoc[uuid] || {})[f] || {}).wrongWords || 0), 0);

  const stressOf = (uuid) => VOICE_FIELDS_KEYS
    .reduce((n, f) => n + (((covByLoc[uuid] || {})[f] || {}).stressOpen || 0), 0);

  const badge = (uuid, f) => {
    const c = (covByLoc[uuid] || {})[f];
    if (!c) return <span className="as-badge as-badge-none" title="no voice-over">·</span>;
    if (!c.hasAlignment) return <span className="as-badge as-badge-audio" title="voice-over, no timings">♪</span>;
    if (c.stale) return <span className="as-badge as-badge-stale" title="text edited after this was voiced">!</span>;
    return <span className="as-badge as-badge-ok" title="timings ready">✓</span>;
  };

  // AudioVersions reads uuid / questId / table straight out of this URL, the
  // same one the point list builds, so no extra wiring is needed.
  const trackUrl = point
    ? `/api/audio/${point.id}/${field}?uuid=${point.uuid}&questId=${encodeURIComponent(questId)}&table=quests_test`
    : null;

  // The bar moves between server events on the local clock.
  const anyRunning = Object.values(alignStatus).some((e) => e && (e.status === 'running' || e.status === 'queued'));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [anyRunning]);
  // eslint-disable-next-line no-unused-vars
  const tickNow = tick;

  const onTrackChanged = useCallback(() => {
    loadVersions(versionId);
    loadCoverage();
  }, [loadVersions, loadCoverage, versionId]);

  return (
    <div className="as-root">
      {notices.length ? (
        <div className="as-notices" role="status" aria-live="polite">
          {notices.map((n) => (
            <button key={n.id} type="button" className={`as-notice as-notice-${n.kind}`}
                    title="dismiss" onClick={() => dismissNotice(n.id)}>{n.text}</button>
          ))}
        </div>
      ) : null}
      <header className="as-head">
        <LinkButton className="back-button" href={backHref} onClick={onBack}>Back to Editor</LinkButton>
        <div className="as-head-title">
          <strong>{questName || questId}</strong>
          {point ? <span className="as-head-point">{pointIndex + 1}. {point.name}</span> : null}
          {point && tasksHref && taskCountsByLocation && taskCountsByLocation[point.uuid] && taskCountsByLocation[point.uuid].total > 0 ? (
            <a className="as-head-tasks" href={tasksHref(point.uuid)} title="The Tasks cards of this point">✅ {taskCountsByLocation[point.uuid].open || taskCountsByLocation[point.uuid].total} task{taskCountsByLocation[point.uuid].total === 1 ? '' : 's'}</a>
          ) : null}
        </div>
        <div className="as-head-controls">
          {FIELDS.map((f) => (
            <button
              key={f.key}
              className={`as-tab ${field === f.key ? 'as-tab-on' : ''}`}
              onClick={() => setField(f.key)}
              title={fieldHasText(point, f.key) ? undefined : 'This point has no text in this field'}
            >
              {f.label}{point && !fieldHasText(point, f.key) ? ' · empty' : ''}
            </button>
          ))}
          <select
            className="as-select"
            value={versionId || ''}
            onChange={(e) => setVersionId(Number(e.target.value) || null)}
            disabled={!versions.length}
          >
            {versions.length === 0 ? <option value="">no versions</option> : null}
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {versionName(v)}{v.is_active ? ' · active' : ''}
              </option>
            ))}
          </select>
          {versionId && versions.some((v) => v.id === versionId && !v.is_active && !v.is_fragment) ? (
            <button
              className="as-ghost"
              title="Make this the version the app plays"
              onClick={async () => {
                await fetch(`/api/audio-versions/${versionId}/activate`,
                  { method: 'POST', credentials: 'include' });
                loadVersions(versionId);
                loadCoverage();
              }}
            >
              ★ Make active
            </button>
          ) : null}
          <button
            className="as-ghost"
            onClick={runAlign}
            disabled={!versionId || (align_ && (align_.status === 'running' || align_.status === 'queued'))}
            title="Measure word timings for the selected version"
          >
            <StableLabel
              current={align_ && align_.status === 'queued'
                ? `In the queue (${align_.queued})`
                : align_ && align_.status === 'running'
                  ? `Aligning… ${align_.elapsed || 0}s`
                  : (alignment ? 'Re-align' : 'Align this version')}
              sizes={['Align this version', 'Re-align', 'In the queue (00)', 'Aligning… 000s']}
            />
          </button>
          {point && onNavigateToLocation ? (
            <button className="as-ghost" onClick={() => onNavigateToLocation(questId, point.uuid)}>
              Open in list
            </button>
          ) : null}
        </div>
      </header>
      <AlignProgress entry={versionId ? alignStatus[versionId] : null} what="This version" />

      <div className="as-body">
        <nav className="as-rail">
          {points.map((p, i) => (
            <button
              key={p.uuid}
              className={`as-rail-item ${p.uuid === locUuid ? 'as-rail-on' : ''}`}
              onClick={() => setLocUuid(p.uuid)}
              title={p.name}
            >
              <span className="as-rail-n">{i + 1}</span>
              <span className="as-rail-name">{p.name}</span>
              <span className="as-rail-badges">
                {badge(p.uuid, 'description')}
                {badge(p.uuid, 'after_message')}
                {wrongOf(p.uuid) ? (
                  <span className="as-badge as-badge-wrong"
                        title="words heard and marked as not said the way the text spells them">
                    ✗{wrongOf(p.uuid)}
                  </span>
                ) : null}
                {stressOf(p.uuid) ? (
                  <span className="as-badge as-badge-stress"
                        title="stress mismatches the model found that nobody has listened to yet (Review tab)">
                    ≠{stressOf(p.uuid)}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </nav>

        <section className="as-text-pane">
          {alignment && textDiff && textDiff.differ > 0 && !asVoiced ? (
            <div className="as-warn">
              <strong>! Text changed since this was voiced — {textDiff.differ} word{textDiff.differ === 1 ? '' : 's'} differ.</strong>{' '}
              Changed words are underlined (hover to see how they were read), dropped words are
              struck through, new words are faint and have no audio. «As voiced» shows the take's own text.
            </div>
          ) : null}
          {donorMap ? (
            donorMap.changed ? (
              <div className="as-warn">
                <strong>! Text changed since this fragment was voiced.</strong> Only the words
                that still match are lit; the rest has no counterpart in the source.
              </div>
            ) : (
              <div className="as-note">
                The source voices only the lit part of the text
                {donorMap.speaker ? `, read by Speaker ${donorMap.speaker}` : ''} — select within it.
              </div>
            )
          ) : null}
          {loading ? <div className="as-note">Loading…</div> : null}
          {error ? <div className="as-warn"><strong>!</strong> {error}</div> : null}
          {readingError ? <div className="as-warn"><strong>!</strong> {readingError}</div> : null}
          {align_ && align_.status === 'failed' ? (
            <div className="as-warn">
              <strong>!</strong> {align_.error || 'The alignment run failed.'}
              {align_.output ? (
                <details><summary>run output</summary><pre className="as-pre">{align_.output}</pre></details>
              ) : null}
            </div>
          ) : null}
          {point && !versionId && !loading ? (
            <div className="as-note">
              {fieldHasText(point, field)
                ? 'No voice-over for this field yet — make one on the Create voice-over tab.'
                : `This point has no ${field === 'after_message' ? 'after message' : 'description'}.`}
            </div>
          ) : null}
          {!loading && !error && !alignment && data ? (
            <div className="as-note">
              Alignment has not been run on this version yet — press «Measure word timings» above.
              Until it finishes, the text below is shown as plain prose.
            </div>
          ) : null}
          <div className="as-text" ref={textBox} onMouseUp={onTextMouseUp}
               onScroll={() => { if (follow) setFollow(false); }}>
            {rendered && rendered.nodes}
          </div>
          {rendered && rendered.used.size ? (
            <div className="as-legend">
              {LEGEND.filter((l) => rendered.used.has(l.cls)).map((l) => (
                <span key={l.cls} className="as-legend-item">
                  <span className={l.cls}>{l.sample}</span> {l.label}
                </span>
              ))}
            </div>
          ) : null}
          {!follow && words.length ? (
            <button className="as-refollow" onClick={() => { setFollow(true); }}>
              ↓ Follow playback
            </button>
          ) : null}
        </section>

        <section className="as-audio-pane">
          <audio
            ref={audioRef}
            preload="auto"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
          />
          <div className="as-wave-wrap">
            <div className="as-wave" ref={waveBox} />
            {loop && duration > 0 ? (
              <span
                className="as-span"
                style={{ left: `${(loop.start / duration) * 100}%`,
                         width: `${((loop.end - loop.start) / duration) * 100}%` }}
              />
            ) : null}
          </div>

          <div className="as-strip" title="phrases on the time axis">
            {duration > 0 && phrases.map((p, pi) => {
              const a = words[p[0]].s;
              const b = words[p[p.length - 1]].e;
              return (
                <span
                  key={pi}
                  className={`as-chip ${pi === activePhrase ? 'as-chip-live' : ''}`}
                  style={{ left: `${(a / duration) * 100}%`, width: `${((b - a) / duration) * 100}%` }}
                  onClick={() => seek(a)}
                />
              );
            })}
          </div>

          <div className="as-transport">
            <button className="as-play" onClick={toggle} disabled={!src}>
              {playing ? '❚❚' : '▶'}
            </button>
            <span className="as-clock">{fmt(time)} / {fmt(duration)}</span>
            <button
              className={`as-ghost${asVoiced ? ' as-ghost-on' : ''}`}
              onClick={() => setAsVoiced((v) => !v)}
              title={asVoiced ? 'Showing the text this take was voiced from — click for the text as it is today' : 'Showing the text as it is today over this take — click for the text as voiced'}
            >
              {asVoiced ? 'as voiced' : 'current text'}
            </button>
            <span className="as-rates">
              {[0.75, 1, 1.25, 1.5].map((r) => (
                <button key={r} className={`as-rate ${rate === r ? 'as-rate-on' : ''}`}
                        onClick={() => setRate(r)}>{r}×</button>
              ))}
            </span>
            <button className="as-ghost" onClick={playLoop} disabled={!loop}>
              {loop ? `↻ ${(loop.end - loop.start).toFixed(1)}s` : '↻ selection'}
            </button>
            {loop ? <button className="as-ghost" onClick={() => setLoop(null)}>clear</button> : null}
            {stressMismatches.length ? (
              <button className={`as-ghost as-stress-btn${stressOnly ? ' as-ghost-on' : ''}`}
                      onClick={() => setStressOnly((v) => !v)}
                      title="Words where the stress heard differs from the one the text expects (machine check). Click to dim the rest.">
                stress ≠ {stressMismatches.length}
              </button>
            ) : null}
          </div>
          {stressOnly && stressMismatches.length ? (
            <div className="as-stress-list">
              {stressMismatches.map(({ wi, r }) => {
                const w = words[wi]; const voiced = text.slice(w.a, w.b);
                return (
                  <button key={wi} className="as-stress-item" onClick={() => seek(Math.max(0, w.s - 0.6))}
                          title={r.src === 'acute' ? 'expected stress from the glossary / a mark in the text' : 'expected stress from RUAccent + silero-stress'}>
                    <span className="as-stress-exp">{withStress(voiced, r.exp)}</span>
                    {' → '}
                    <span className="as-stress-heard">{withStress(voiced, r.heard)}</span>
                    <span className="as-stress-conf">{r.conf != null ? r.conf.toFixed(2) : ''}{r.src === 'acute' ? ' · gl' : ''}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="as-tabs">
            <button className={`as-tab ${tab === 'splice' ? 'as-tab-on' : ''}`}
                    onClick={() => setTab('splice')}>Editor</button>
            <button className={`as-tab ${tab === 'tts' ? 'as-tab-on' : ''}`}
                    onClick={() => setTab('tts')}>Create voice-over</button>
            <button className={`as-tab ${tab === 'review' ? 'as-tab-on' : ''}`}
                    onClick={() => setTab('review')}
                    title="Stress mismatches the model found, to be heard and judged">
              Review{coverage.reduce((n, c) => n + (c.stressOpen || 0), 0) ? ` · ${coverage.reduce((n, c) => n + (c.stressOpen || 0), 0)}` : ''}
            </button>
          </div>
          {tab === 'review' ? (
            <StressReview
              questId={questId}
              locations={points}
              playingKey={playingKey}
              onOpen={openReviewRow}
              onChanged={loadCoverage}
            />
          ) : null}
          {tab === 'tts' && point ? (
            <SpeechGeneration
              initialData={ttsData}
              onBack={() => setTab('splice')}
              onSaveVoiceoverText={onSaveVoiceoverText}
              // Refresh the lists around it, but leave the panel standing: it
              // reports the finished take itself, with a player for it, and
              // switching away here tore that report down as it appeared.
              onAudioUploaded={() => { loadVersions(); loadCoverage(); }}
            />
          ) : null}

          {tab === 'splice' && point ? (
            <div className="as-newtake">
              <button
                className="as-ghost"
                onClick={() => newTake()}
                disabled={take && take.status === 'running'}
                title="Voice the same text again with the last used settings; the active version stays as it is"
              >
                <StableLabel
                  current={take && take.status === 'running' && !take.fragment
                    ? `Generating new voiceover… ${take.elapsed || 0}s` : '+ Generate new voiceover'}
                  sizes={['+ Generate new voiceover', 'Generating new voiceover… 000s']}
                />
              </button>
              {take && take.status === 'done' ? (() => {
                // Follows the take's timings live, so it says when the take
                // can actually be used as a donor.
                const entry = alignStatus[take.versionId];
                const st = entry && entry.status;
                const failed = st === 'failed';
                const what = `${take.fragment ? 'Fragment version' : 'Version'} ${take.versionId}`;
                if (st === 'running' || st === 'queued') {
                  return <AlignProgress entry={entry} what={`${what} stored (not active)`} flat />;
                }
                const msg = st === 'done'
                  ? (take.fragment
                    ? `✓ ${what} is ready — opened as the source below.`
                    : `✓ ${what} is ready — pick it as the source below.`)
                  : failed
                    ? `! ${what} stored, but its timings failed.`
                    : `✓ ${what} stored (not active) · waiting for timings…`;
                return (
                  <span className={failed ? 'as-warn as-note-flat' : 'as-note as-note-flat'}>
                    {msg}
                    {failed && entry && entry.error ? ` ${entry.error}` : ''}
                    {failed && entry && entry.output ? (
                      <details><summary>run output</summary><pre className="as-pre">{entry.output}</pre></details>
                    ) : null}
                  </span>
                );
              })() : null}
              {take && take.status === 'failed' ? (
                <span className="as-warn as-note-flat">
                  <strong>!</strong> {take.error}
                  {take.details ? (
                    <details><summary>details</summary><pre className="as-pre">{
                      typeof take.details === 'string' ? take.details : JSON.stringify(take.details, null, 2)
                    }</pre></details>
                  ) : null}
                </span>
              ) : null}
            </div>
          ) : null}

          {tab === 'splice' && point && versionId ? (
            <AudioEditorModal
              // A fresh editor per version: it loads its track once, on mount,
              // and without this a version picked in the header kept the
              // previous version's audio under the new version's timings.
              key={`${versionId}:${editorNonce}`}
              inline
              questId={questId}
              locationId={point.id}
              fieldType={field}
              uuid={point.uuid}
              table="quests_test"
              versions={versions}
              editVersion={versions.find((v) => v.id === versionId) || versions[0]}
              align={align}
              selection={loop}
              donorRequest={donorRequest}
              onDonorMap={setDonorMap}
              draftTarget={initialDraft}
              onClose={() => setEditorNonce((n) => n + 1)}
              onSaved={() => { loadVersions(); loadCoverage(); }}
            />
          ) : null}
          {tab === 'splice' && point ? (
            <div className="as-track">
              <AudioUploader
                locationId={point.id}
                fieldType={field}
                questId={questId}
                existingAudioUrl={trackUrl}
                onAudioUploaded={onTrackChanged}
              />
              <AudioVersions
                locationId={point.id}
                fieldType={field}
                audioUrl={trackUrl}
                hideEditor
                onVersionsChanged={onTrackChanged}
              />
            </div>
          ) : null}

        </section>
      </div>

      {popupAt && loop ? (
        <SelectionPopup
          at={popupAt}
          questId={questId}
          wordCount={loop.wi1 - loop.wi0 + 1}
          fragPlan={fragPlan}
          take={take}
          word={selectedWord}
          onReadingChanged={() => setReadingTick((n) => n + 1)}
          onPlay={() => playSelection(false)}
          onVoice={(plan) => newTake(plan || fragPlan)}
          onClose={() => setPopupAt(null)}
          // A new glossary and a re-marked text arrive on their own (the
          // server announces both); what needs a nudge is the per-point
          // counters.
          onGlossaryApplied={loadCoverage}
          onVerdictChanged={loadCoverage}
        />
      ) : null}
    </div>
  );
}
